import React, { useState } from "react";
import { GraduationCap, Briefcase, ArrowUpRight, Send } from "lucide-react";
import CareersApplicationModal from "@/components/site/CareersApplicationModal";

const POSITIONS = [
  {
    icon: GraduationCap,
    kind: "internship",
    badge: "Internship",
    title: "Law Student Internships",
    sub: "3 – 5 year programme · 4 to 8 weeks",
    body: "We accept a small number of interns each cycle from recognised law schools. Interns work alongside the advocate on record on live briefs — research, drafting, attending hearings and conferences. Stipend on merit.",
    looking: ["Strong drafting", "Independent research", "Discipline & confidentiality"],
    cta: "Apply for an Internship",
  },
  {
    icon: Briefcase,
    kind: "appointment",
    badge: "Appointment",
    title: "Junior Counsel & Associate Appointments",
    sub: "Enrolled advocates · 0 – 3 years standing",
    body: "We make occasional appointments at the junior counsel level. The position is hands-on: drafting, court appearances, conferences and case management — under direct mentorship of the senior advocate.",
    looking: ["Enrolled with the Bar", "Litigation orientation", "Sound legal writing"],
    cta: "Apply for an Appointment",
  },
];

export default function Careers() {
  const [openKind, setOpenKind] = useState(null);

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="section"
      style={{ backgroundColor: "#F9F6F0", borderTop: "1px solid #E2DCD0" }}
    >
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-7">
            <span className="ornament text-xs" style={{ letterSpacing: "0.28em", textTransform: "uppercase" }}>
              Careers
            </span>
            <h2
              className="font-serif-display mt-6 text-4xl md:text-5xl"
              style={{ color: "#0A1128", fontWeight: 500, lineHeight: 1.1 }}
            >
              Join a practice that
              <br />
              <span style={{ fontStyle: "italic", color: "#7A2021" }}>
                takes its trade seriously.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg" style={{ color: "#334155", lineHeight: 1.7 }}>
              We work in small teams, with senior oversight on every brief. If you want to learn
              litigation as a craft — by doing — we would be glad to hear from you.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-px"
          style={{ backgroundColor: "#E2DCD0", border: "1px solid #E2DCD0" }}
        >
          {POSITIONS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.kind}
                className="p-8 md:p-10 flex flex-col gap-6"
                style={{ backgroundColor: "#FFFFFF" }}
                data-testid={`careers-card-${p.kind}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-[10px] uppercase px-3 py-1"
                    style={{
                      letterSpacing: "0.22em",
                      color: "#7A2021",
                      border: "1px solid #C5A059",
                      backgroundColor: "rgba(197,160,89,0.08)",
                    }}
                  >
                    {p.badge}
                  </span>
                  <Icon size={26} strokeWidth={1.4} style={{ color: "#7A2021" }} />
                </div>

                <div>
                  <h3
                    className="font-serif-display text-2xl md:text-3xl"
                    style={{ color: "#0A1128", fontWeight: 500, lineHeight: 1.15 }}
                  >
                    {p.title}
                  </h3>
                  <div
                    className="mt-2 text-[11px] uppercase"
                    style={{ color: "#64748B", letterSpacing: "0.22em" }}
                  >
                    {p.sub}
                  </div>
                </div>

                <p className="text-base" style={{ color: "#334155", lineHeight: 1.7 }}>
                  {p.body}
                </p>

                <div>
                  <div
                    className="text-[10px] uppercase mb-3"
                    style={{ color: "#7A2021", letterSpacing: "0.22em" }}
                  >
                    What we look for
                  </div>
                  <ul className="space-y-1.5">
                    {p.looking.map((l) => (
                      <li
                        key={l}
                        className="flex items-center gap-2 text-xs uppercase"
                        style={{ color: "#0A1128", letterSpacing: "0.14em" }}
                      >
                        <span
                          className="inline-block w-3 h-px"
                          style={{ backgroundColor: "#C5A059" }}
                        />
                        {l}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-4" style={{ borderTop: "1px solid #E2DCD0" }}>
                  <button
                    type="button"
                    onClick={() => setOpenKind(p.kind)}
                    className="btn-primary"
                    data-testid={`careers-apply-${p.kind}`}
                  >
                    <Send size={14} strokeWidth={2} />
                    {p.cta}
                    <ArrowUpRight size={14} strokeWidth={2} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-sm" style={{ color: "#64748B" }}>
          We respond only to applications that match an active opening. Please attach a résumé
          (PDF or Word, up to 5 MB) and a brief cover note.
        </p>
      </div>

      <CareersApplicationModal
        open={openKind !== null}
        onClose={() => setOpenKind(null)}
        positionType={openKind || "internship"}
      />
    </section>
  );
}
